import React, { useCallback, useState } from 'react';
import { TextBlock, TypeOfText } from '../text/TextBlock';
import {
  Address,
  ArrowIcon,
  Badge,
  ButtonReStartApp,
  ButtonStyles,
  CardBody,
  CardContent,
  CardHeader,
  Container,
  Info,
  Label,
  SubTitle,
  TitleContainer,
  WrapperButtons,
} from './ResumeApplications.styles';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useNavigate } from 'react-router-dom';
import LoanApplicationReducer from '@/reducers/LoanApplication';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { toPascalCase } from '@/utils/capitalizeWords';
import { setIsOpenResume } from '@/reducers/currentPage';
import { formatOccupancyDateToUI } from '@/utils/handleDate';

interface LoanApplicationItem {
  loanId: string;
  loanNumber: string;
  borrowerProperty: {
    propertyStreetAddress: string;
    propertyCity: string;
    propertyState: string;
    propertyZip: string;
    propertyCounty?: string;
  };
  borrowers: Array<{
    borrowerLoanPurpose: string;
    borrowerFirstName: string;
    borrowerLastName: string;
    borrowerEmailAddress: string;
    borrowerPreferredContactMethod: string;
    borrowerMaritalStatus: string;
    borrowerPrimaryPhone: string;
    borrowerCitizenship: string;
    creditScore: number;
    otherIncome: {
      incomeType: string;
      grossMonthlyIncome: number;
    }[];
  }>;
  propertyPrimaryUse: string;
  propertyType: string;
  isAppCompleted: boolean;
  boappLastStep?: string;
  applicationDate?: string;
  [key: string]: any;
}

interface ResumeApplicationsProps {
  applications: LoanApplicationItem[];
  prefillEmail?: string;
  setStep?: (step: number) => void;
  canStartNewApp?: boolean;
}

interface ApplicationCardProps {
  application: LoanApplicationItem;
  isCompleted: boolean;
  isSelected: boolean;
  onSelect: (application: LoanApplicationItem) => void;
  onGotoBorrowerPortal: () => void;
  onResumeApplication: (application: LoanApplicationItem) => Promise<void>;
}

const ApplicationCard: React.FC<ApplicationCardProps> = ({ application, isCompleted, isSelected, onSelect, onGotoBorrowerPortal, onResumeApplication }) => {
  const addressParts = [
    application?.borrowerProperty?.propertyStreetAddress,
    application?.borrowerProperty?.propertyCity,
    application?.borrowerProperty?.propertyState,
    application?.borrowerProperty?.propertyZip,
  ].filter(Boolean);

  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(application);
  };

  const handleActionClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSelected) {
      return;
    }

    if (isCompleted) {
      onGotoBorrowerPortal();
    } else {
      await onResumeApplication(application);
    }
  };

  return (
    <CardContent key={application.loanId} data-loan-id={application.loanId} data-loan-number={application.loanNumber}>
      <CardHeader>
        <SubTitle>{isCompleted ? 'Access Your Borrower Portal' : 'Your Resumable Applications'}</SubTitle>
        <Label>{isCompleted ? 'You have an active loan in your Borrower Portal. View and complete open tasks.' : 'Select an application to resume.'}</Label>
      </CardHeader>
      <CardBody $isCompleted={isCompleted} $isSelected={isSelected} onClick={handleCardClick}>
        <Address>{addressParts.join(', ')}</Address>
        {isCompleted && <Badge>Loan in progress</Badge>}
        <Info>Loan #{application.loanNumber}</Info>
      </CardBody>
      <WrapperButtons>
        <ButtonStyles onClick={handleActionClick} data-loan-id={application.loanId} $isSelected={isSelected}>
          {isCompleted ? 'Borrower Portal' : 'Resume Application'} <ArrowIcon />
        </ButtonStyles>
      </WrapperButtons>
    </CardContent>
  );
};

export const ResumeApplications: React.FC<ResumeApplicationsProps> = ({ applications, prefillEmail, setStep, canStartNewApp }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const resume = useAppSelector((state) => state.TemplateConfig.features?.resume);
  const loanApplication = useAppSelector((state) => state.LoanApplication);
  const resumeOnly = useAppSelector((state) => state.TemplateConfig.features?.resume?.config?.resumeOnly ?? false);
  const [selectedApplication, setSelectedApplication] = useState<LoanApplicationItem | null>(null);
  const shouldShowRestartButton = useCallback((): boolean => {
    if (resumeOnly) return false;
    return true;
  }, [resumeOnly]);

  const fetchCoordinatesFromAddress = useCallback(async (address: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      if (!address) return null;

      const results = await geocodeByAddress(address);
      if (results && results.length > 0) {
        const coordinates = await getLatLng(results[0]);
        return coordinates;
      }
      return null;
    } catch (error) {
      console.error('Error fetching coordinates:', error);
      return null;
    }
  }, []);

  const prefillLoanApplicationData = useCallback(
    async (data: any) => {
      const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${month}/${day}/${year}`;
      };

      const cleanMailingAddress = (mailing: any) => {
        if (!mailing) return {};

        return {
          mailingStreetAddress: toPascalCase(mailing.mailingStreetAddress || ''),
          mailingStreetAddress2: toPascalCase(mailing.mailingStreetAddress2 || ''),
          mailingCity: toPascalCase(mailing.mailingCity || ''),
          mailingState: toPascalCase(mailing.mailingState || ''),
          mailingCountry: toPascalCase(mailing.mailingCountry || ''),
          mailingCounty: toPascalCase(mailing.mailingCounty || ''),
          mailingZip: mailing.mailingZip || '',
          mailingLatitude: Number(mailing.mailingLatitude) || 0,
          mailingLongitude: Number(mailing.mailingLongitude) || 0,
        };
      };

      const buildAddressString = (property: any) => {
        const parts = [property?.propertyStreetAddress, toPascalCase(property?.propertyCity || ''), property?.propertyState, property?.propertyZip].filter(Boolean);
        return parts.join(', ');
      };

      const propertyAddress = buildAddressString(data.borrowerProperty);
      let propertyCoordinates = { lat: 0, lng: 0 };

      if (propertyAddress) {
        const existingLat = Number(data.borrowerProperty?.propertyLatitude) || 0;
        const existingLng = Number(data.borrowerProperty?.propertyLongitude) || 0;

        if (existingLat === 0 || existingLng === 0) {
          const fetchedCoordinates = await fetchCoordinatesFromAddress(propertyAddress);
          if (fetchedCoordinates) {
            propertyCoordinates = fetchedCoordinates;
          }
        } else {
          propertyCoordinates = { lat: existingLat, lng: existingLng };
        }
      }

      // Fetch mailing address coordinates if needed
      let mailingCoordinates = { lat: 0, lng: 0 };
      if (data.mailingAddress && !data.mailingAddress.mailingAddressSameAsProperty) {
        const mailingAddress = [data.mailingAddress?.mailingStreetAddress, data.mailingAddress?.mailingCity, data.mailingAddress?.mailingState, data.mailingAddress?.mailingZip]
          .filter(Boolean)
          .join(', ');

        if (mailingAddress) {
          const existingMailingLat = Number(data.mailingAddress?.mailingLatitude) || 0;
          const existingMailingLng = Number(data.mailingAddress?.mailingLongitude) || 0;

          if (existingMailingLat === 0 || existingMailingLng === 0) {
            const fetchedMailingCoordinates = await fetchCoordinatesFromAddress(mailingAddress);
            if (fetchedMailingCoordinates) {
              mailingCoordinates = fetchedMailingCoordinates;
            }
          } else {
            mailingCoordinates = { lat: existingMailingLat, lng: existingMailingLng };
          }
        }
      }

      const updatedBorrowers = data?.borrowers?.map((borrower: any) => {
        const employmentHistory = borrower?.employmentHistory || [];
        let updatedEmploymentHistory = employmentHistory.map((emp: any) => {
          const startDate = new Date(emp.startDate);
          const today = new Date();
          const monthsDiff = (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth());

          return {
            ...emp,
            employerName: toPascalCase(emp.employerName || ''),
            jobTitle: toPascalCase(emp.jobTitle || ''),
            startDate: formatDate(emp.startDate),
            employedMonths: monthsDiff,
            totalMonths: monthsDiff,
            professionMonths: monthsDiff,
            grossMonthlyIncome: Number(borrower.currentEmploymentGrossMonthlyIncomeTotal) || 0,
          };
        });

        if (employmentHistory.length === 0) {
          updatedEmploymentHistory = [
            {
              currentEmployment: null,
              employerName: '',
              endDate: '',
              grossMonthlyIncome: 0,
              jobTitle: '',
              startDate: '',
              type: '',
            },
          ];
        }

        const { creditScore, ...borrowerWithoutCreditScore } = borrower;
        const borrowerData = {
          ...borrowerWithoutCreditScore,
          borrowerFirstName: toPascalCase(borrower.borrowerFirstName || ''),
          borrowerLastName: toPascalCase(borrower.borrowerLastName || ''),
          borrowerMiddleName: toPascalCase(borrower.borrowerMiddleName || ''),
          ...(borrower.borrowerSuffixName && { borrowerSuffixName: borrower.borrowerSuffixName }),
          borrowerEmailAddress: borrower.borrowerEmailAddress?.toLowerCase() || '',
          borrowerLoanPurpose: toPascalCase(borrower.borrowerLoanPurpose || ''),
          borrowerMaritalStatus: toPascalCase(borrower.borrowerMaritalStatus || ''),
          borrowerPreferredContactMethod: toPascalCase(borrower.borrowerPreferredContactMethod || ''),
          borrowerCitizenship: toPascalCase(borrower.borrowerCitizenship || ''),
          borrowerRelationship: toPascalCase(borrower.borrowerRelationship || ''),
          currentEmploymentGrossMonthlyIncomeTotal: Number(borrower.currentEmploymentGrossMonthlyIncomeTotal) || 0,
          employmentHistory: updatedEmploymentHistory,
          ...(borrower.borrowerDOB && { borrowerDOB: formatDate(borrower.borrowerDOB) }),
          isPrimary: borrower.isPrimary || false,
          isSpouse: borrower.isSpouse || false,
          declaration: borrower.declaration || {},
          governmentHMDA: borrower.governmentHMDA || { applicantRace: [], applicantEthnicity: [], applicantSex: [] },
          creditScore: Number(creditScore) || 0,
          borrowerPrimaryPhone: borrower.borrowerPrimaryPhone || '',
          borrowerSSN: borrower.borrowerSSN || '',
          otherIncome: borrower.otherIncome || [],
          ...(borrower.tradelines && borrower.tradelines.length > 0 && { tradelines: borrower.tradelines }),
        };

        return borrowerData;
      });

      const updatedState = {
        ...loanApplication,
        loanId: data.loanId || '',
        loanNumber: data.loanNumber || '',
        loanType: data.loanType || '',
        estimatedMarketValue: Number(data.equityResponse?.estimatedMarketValue) || Number(data.estimatedMarketValue) || 0,
        remainingMortgageBalance: Number(data.equityResponse?.remainingMortgageBalance) || Number(data.remainingMortgageBalance) || 0,
        estimatedHomeEquity: Number(data.equityResponse?.estimatedHomeEquity) || Number(data.estimatedHomeEquity) || 0,
        estimatedRawEquity: Number(data.equityResponse?.estimatedRawEquity) || Number(data.estimatedRawEquity) || 0,
        lineAmount: Number(data.lineAmount) || Number(data.selectedProgram?.lineAmount) || 0,
        drawAmount: Number(data.drawAmount) || 35000,
        initialDrawAmount: Number(data.drawAmount) || 35000,
        apr: Number(data.apr) || Number(data.selectedProgram?.apr) || 0,
        term: data.term || data.selectedProgram?.term || 'Variable',
        loanTerm: data.loanTerm || data.selectedProgram?.term || '0',
        ipAddress: data.ipAddress || '',
        propertyPrimaryUse: toPascalCase(data.propertyPrimaryUse || ''),
        propertyType: toPascalCase(data.propertyType || ''),
        isResume: true,
        isEligible: false,
        minDrawAmount: 0,
        maxDrawAmount: 0,
        borrowers: updatedBorrowers,
        borrowerProperty: {
          propertyStreetAddress: toPascalCase(data.borrowerProperty?.propertyStreetAddress || ''),
          propertyStreetAddress2: toPascalCase(data.borrowerProperty?.propertyStreetAddress2 || ''),
          propertyCity: toPascalCase(data.borrowerProperty?.propertyCity || ''),
          propertyState: toPascalCase(data.borrowerProperty?.propertyState || ''),
          propertyCountry: toPascalCase(data.borrowerProperty?.propertyCountry || 'United States'),
          propertyCounty: toPascalCase(data.borrowerProperty?.propertyCounty || ''),
          propertyZip: data.borrowerProperty?.propertyZip || '',
          propertyLatitude: propertyCoordinates.lat,
          propertyLongitude: propertyCoordinates.lng,
          propertyOccupancyDate: data.borrowerProperty?.propertyOccupancyDate || '1990-01-01',
          city: toPascalCase(data.borrowerProperty?.city || data.borrowerProperty?.propertyCity || ''),
          state: data.borrowerProperty?.state || data.borrowerProperty?.propertyState || '',
          zip: data.borrowerProperty?.zip || data.borrowerProperty?.propertyZip || '',
        },
        mailingAddress: {
          ...cleanMailingAddress(data.mailingAddress),
          mailingLatitude: mailingCoordinates.lat,
          mailingLongitude: mailingCoordinates.lng,
        },
        consent: {
          ...loanApplication.consent,
          eConsent: data.consent?.eConsent || true,
          eConsentDate: data.consent?.eConsentDate || null,
          tcpaConsent: data.consent?.tcpaConsent || false,
          softPullConsent: data.consent?.softPullConsent || false,
          softPullConsentDate: data.consent?.softPullConsentDate || null,
          employmentConsent: data.consent?.employmentConsent || false,
          eConsentCoborrower: data.consent?.eConsentCoborrower || false,
        },
        selectedProgramName: toPascalCase(data.selectedProgramName || data.selectedProgram?.programName || 'HELOC - Standard'),
        selectedProgram: data.selectedProgram || {},
        availablePrograms: {
          helocData: {
            rates: data.availablePrograms?.helocData?.rates || [],
            ...data.availablePrograms?.helocData,
          },
          helData: {
            rates: data.availablePrograms?.helData?.rates || [],
            ...data.availablePrograms?.helData,
          },
          ...data.availablePrograms,
        },
        propertyIsMailingAddress: true,
        fixedLineAmount: 35000,
        cltv: Number(data.cltv) || 0,
        lenderName: toPascalCase(data.lenderName || ''),
        programType: data.programType?.toUpperCase() || 'HELOC',
      };

      dispatch({
        type: 'LoanApplication/updateLoanApplication',
        payload: updatedState,
      });

      const primaryBorrower = data.borrowers?.find((b: any) => b.isPrimary) || data.borrowers?.[0];

      // Determine primary phone and type
      type PhoneData = {
        phone: string;
        selectedPhoneType: 'mobilePhone' | 'workPhone' | 'homePhone';
        mobilePhone: string;
        homePhone: string;
        workPhone: string;
      };
      const getPhoneData = (): PhoneData => {
        if (!primaryBorrower) {
          return { phone: '', selectedPhoneType: 'mobilePhone', mobilePhone: '', homePhone: '', workPhone: '' };
        }
        const { borrowerPrimaryPhone, borrowerWorkPhone, borrowerAlternatePhone } = primaryBorrower;
        // Define priority order
        if (borrowerPrimaryPhone) {
          return {
            phone: borrowerPrimaryPhone,
            selectedPhoneType: 'mobilePhone',
            mobilePhone: borrowerPrimaryPhone,
            homePhone: borrowerAlternatePhone || '',
            workPhone: borrowerWorkPhone || '',
          };
        }
        if (borrowerWorkPhone) {
          return {
            phone: borrowerWorkPhone,
            selectedPhoneType: 'workPhone',
            mobilePhone: '',
            homePhone: borrowerAlternatePhone || '',
            workPhone: borrowerWorkPhone,
          };
        }
        if (borrowerAlternatePhone) {
          return {
            phone: borrowerAlternatePhone,
            selectedPhoneType: 'homePhone',
            mobilePhone: '',
            homePhone: borrowerAlternatePhone,
            workPhone: '',
          };
        }
        return { phone: '', selectedPhoneType: 'mobilePhone', mobilePhone: '', homePhone: '', workPhone: '' };
      };

      const phoneData = getPhoneData();

      const currentFormPayload = {
        firstName: toPascalCase(primaryBorrower?.borrowerFirstName || ''),
        middleName: toPascalCase(primaryBorrower?.borrowerMiddleName || ''),
        lastName: toPascalCase(primaryBorrower?.borrowerLastName || ''),
        email: primaryBorrower?.borrowerEmailAddress?.toLowerCase() || '',
        purposeOfLoan: toPascalCase(primaryBorrower?.borrowerLoanPurpose || ''),
        address: data.borrowerProperty?.propertyStreetAddress || '',
        suit: data.borrowerProperty?.propertyStreetAddress2 || '',
        city: toPascalCase(data.borrowerProperty?.propertyCity || ''),
        state: data.borrowerProperty?.propertyState || '',
        zip: data.borrowerProperty?.propertyZip || '',
        mailingCity: toPascalCase(data.mailingAddress?.mailingCity || ''),
        mailingState: data.mailingAddress?.mailingState || '',
        mailingZip: data.mailingAddress?.mailingZip || '',
        primaryUse: toPascalCase(data.propertyPrimaryUse || ''),
        propertyType: toPascalCase(data.propertyType || ''),
        dataOccupancyDate: formatOccupancyDateToUI(data.borrowerProperty?.propertyOccupancyDate || '1990-01-01'),
        mobile: primaryBorrower?.borrowerPrimaryPhone || '',
        home: primaryBorrower?.borrowerAlternatePhone || '',
        work: primaryBorrower?.borrowerWorkPhone || '',
        // Add phone fields for proper mapping
        phone: phoneData.phone,
        selectedPhoneType: phoneData.selectedPhoneType,
        mobilePhone: phoneData.mobilePhone,
        homePhone: phoneData.homePhone,
        workPhone: phoneData.workPhone,
        eConsent: data.consent?.eConsent || true,
        tcpaConsent: data.consent?.tcpaConsent || false,
        softPullConsent: data.consent?.softPullConsent || false,
        employmentConsent: data.consent?.employmentConsent || false,
        eConsentCoborrower: data.consent?.eConsentCoborrower || false,
        isMailingAddressSame: data.mailingAddress?.mailingAddressSameAsProperty || false,
        propertyOccupancyDate: formatOccupancyDateToUI(data.borrowerProperty?.propertyOccupancyDate || '1990-01-01'),
        propertyLatitude: propertyCoordinates.lat,
        propertyLongitude: propertyCoordinates.lng,
        propertyStreetAddress2: data.borrowerProperty?.propertyStreetAddress2 || '',
        propertyCountry: toPascalCase(data.borrowerProperty?.propertyCountry || 'United States'),
        propertyCounty: toPascalCase(data.borrowerProperty?.propertyCounty || ''),
        propertyZip: data.borrowerProperty?.propertyZip || '',
      };

      dispatch({
        type: 'CurrentForm/addField',
        payload: currentFormPayload,
      });
    },
    [dispatch, loanApplication, fetchCoordinatesFromAddress],
  );

  // Helper function to map boappLastStep to page routes
  const getPageRouteFromBoappLastStep = useCallback((boappLastStep: string): string | null => {
    const pageMapping: Record<string, string> = {
      LetsGetStarted: '/get-started-address',
      googleAddressVerification: '/get-started-address',
      EstimatedAvailableEquity: '/estimated-available-equity',
      Vmo: '/estimated-available-equity',
      VmoLineAmount: '/estimated-available-equity',
      VmoDrawAmount: '/estimated-available-equity',
      PersonalInformation: '/personal-information',
      EmploymentInformation: '/employment-information',
      GovernmentMonitoring: '/government-monitoring-information',
      Declarations: '/declarations',
      InquirySummary: '/inquiry-summary',
      Completion: '/completion',
    };

    return pageMapping[boappLastStep] || null;
  }, []);

  // Helper function to mark all previous pages as accessed
  const markAllPreviousPagesAsAccessed = useCallback((boappLastStep: string): void => {
    const pageOrder = [
      'get-started',
      'get-started-address',
      'estimated-available-equity',
      'vmo',
      'vmo-line-amount',
      'vmo-draw-amount',
      'personal-information',
      'employment-information',
      'government-monitoring-information',
      'declarations',
      'inquiry-summary',
      'completion',
    ];

    const boappLastStepToPageRoute: Record<string, string> = {
      GetStarted: 'get-started',
      GetStartedAddress: 'get-started-address',
      EstimatedAvailableEquity: 'estimated-available-equity',
      Vmo: 'vmo',
      VmoLineAmount: 'vmo-line-amount',
      VmoDrawAmount: 'vmo-draw-amount',
      PersonalInformation: 'personal-information',
      EmploymentInformation: 'employment-information',
      GovernmentMonitoringInformation: 'government-monitoring-information',
      Declarations: 'declarations',
      InquirySummary: 'inquiry-summary',
      Completion: 'completion',
    };

    const targetPageRoute = boappLastStepToPageRoute[boappLastStep];
    if (!targetPageRoute) return;

    const targetIndex = pageOrder.indexOf(targetPageRoute);
    if (targetIndex === -1) return;
  }, []);

  const handleStartNewApplication = () => {
    if (resume?.config?.newPropertyAddress) {
      setStep && setStep(3);
      dispatch({
        type: 'TemplateConfig/updateTemplateConfig',
        payload: {
          isStartingFromNewAddress: true,
        },
      });
    } else {
      const initialState = LoanApplicationReducer(undefined, { type: 'unknown' });
      dispatch({ type: 'LoanApplication/addValue', payload: initialState });
      dispatch({ type: 'CurrentForm/resetForm' });
      if (prefillEmail) {
        dispatch({
          type: 'CurrentForm/addField',
          payload: { email: prefillEmail },
        });
      }
      dispatch({ type: 'CurrentPage/setIsResume', payload: false });
      dispatch({ type: 'CurrentPage/setIsNewApplication', payload: true });
      dispatch(setIsOpenResume(false));
    }
  };

  const handleSelectApplication = (application: LoanApplicationItem) => {
    setSelectedApplication(application);
  };

  const handleResumeApplication = async (application: LoanApplicationItem) => {
    try {
      await prefillLoanApplicationData(application);

      const boappLastStep = application?.boappLastStep;

      if (boappLastStep) {
        const pageRoute = getPageRouteFromBoappLastStep(boappLastStep);

        if (pageRoute) {
          markAllPreviousPagesAsAccessed(boappLastStep);
          navigate(pageRoute);
        } else {
          navigate('/get-started');
        }
      } else {
        navigate('/get-started');
      }

      dispatch({ type: 'CurrentPage/setIsResume', payload: true });
      dispatch({ type: 'CurrentPage/setIsNewApplication', payload: false });
      dispatch(setIsOpenResume(false));
    } catch (error) {
      navigate('/get-started');
    }
  };

  const handleGotoBorrowerPortal = () => {
    try {
      const url = new URL(window.location.href);
      const baseUrl = '/borrowerportal/';
      window.location.href = `${url.origin}${baseUrl}`;
    } catch (error) {
      console.error('Failed to navigate to Borrower Portal:', error);
    }
  };

  return (
    <Container>
      <TitleContainer>
        <TextBlock text="Your Applications" direction={'center'} color="textPrimary" type={TypeOfText.H2} />
      </TitleContainer>
      {applications.map((application) => (
        <ApplicationCard
          key={application.loanId}
          application={application}
          isCompleted={application.isAppCompleted ?? false}
          isSelected={selectedApplication?.loanId === application.loanId}
          onSelect={handleSelectApplication}
          onGotoBorrowerPortal={handleGotoBorrowerPortal}
          onResumeApplication={handleResumeApplication}
        />
      ))}
      {!resumeOnly && shouldShowRestartButton() && canStartNewApp && (
        <ButtonReStartApp>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleStartNewApplication();
            }}
          >
            {resumeOnly && resume?.config?.newPropertyAddress ? 'New Property Address? Verify New Application' : "Start a new application? Let's Get Started"}
          </button>
        </ButtonReStartApp>
      )}
    </Container>
  );
};
